import { useState } from 'react';
import Navigation from '../../components/feature/Navigation';
import { useSEO } from '../../utils/seo';

export default function Archive() {
  const [expandedYear, setExpandedYear] = useState<number | null>(null);

  // SEO Configuration
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  useSEO({
    title: '时间档案 - 懂藏普洱茶陈化记录 | 品鉴历程与转化笔记',
    description: '懂藏时间档案,记录每一款普洱茶的陈化历程。从茶山到茶杯,见证时间的艺术。包含品鉴笔记、转化记录、仓储环境等详细信息。',
    keywords: '懂藏,时间档案,普洱茶陈化,品鉴笔记,茶叶转化,仓储记录,陈年普洱',
    canonical: `${siteUrl}/archive`,
    ogType: 'article',
    schema: {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "时间档案 - 懂藏普洱茶陈化记录",
      "description": "记录每一款普洱茶的陈化历程,见证时间的艺术",
      "author": {
        "@type": "Organization",
        "name": "懂藏"
      },
      "publisher": {
        "@type": "Organization",
        "name": "懂藏",
        "logo": {
          "@type": "ImageObject",
          "url": "https://static.readdy.ai/image/2e1ac11483faca2c19cd44d7d582b015/ce28744b70aae809fa456af269a7a54e.png"
        }
      }
    }
  });

  const timelineData = [
    {
      year: 2015,
      season: '春茶采摘',
      climate: { temp: '18-22°C', humidity: '75-85%', rainfall: '充沛' },
      stage: '初制完成',
      transformation: '茶多酚含量峰值,苦涩度较高',
      microscopy: '细胞壁完整,叶绿素丰富'
    },
    {
      year: 2017,
      season: '第一次转化',
      climate: { temp: '恒温20°C', humidity: '恒湿70%', rainfall: '仓储环境' },
      stage: '初步陈化',
      transformation: '茶多酚开始氧化,涩感降低30%',
      microscopy: '细胞壁开始软化,色素转变'
    },
    {
      year: 2020,
      season: '中期转化',
      climate: { temp: '恒温20°C', humidity: '恒湿70%', rainfall: '仓储环境' },
      stage: '风味成型',
      transformation: '陈香初现,甜度提升,口感圆润',
      microscopy: '细胞结构重组,多糖类物质增加'
    },
    {
      year: 2024,
      season: '当前状态',
      climate: { temp: '恒温20°C', humidity: '恒湿70%', rainfall: '仓储环境' },
      stage: '适饮期',
      transformation: '陈香浓郁,甘甜醇厚,层次丰富',
      microscopy: '细胞完全转化,香气物质稳定'
    },
    {
      year: 2034,
      season: '预期巅峰',
      climate: { temp: '恒温20°C', humidity: '恒湿70%', rainfall: '仓储环境' },
      stage: '极致陈化',
      transformation: '预计达到最佳品饮状态',
      microscopy: '理论模型预测中'
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#E5E4E2]">
      <Navigation />
      
      <div className="pt-24 md:pt-28 lg:pt-32 pb-16 md:pb-20 lg:pb-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1200px] mx-auto">
          {/* Header */}
          <div className="mb-16 md:mb-20 lg:mb-24 text-center">
            <div className="text-[9px] md:text-[10px] text-[#E5E4E2] opacity-50 tracking-widest mb-3 md:mb-4">
              TIME ARCHIVE
            </div>
            <h1 className="text-[18px] md:text-[20px] lg:text-[24px] text-[#D4AF37] font-[500] tracking-wide mb-4 md:mb-6">
              时间档案馆
            </h1>
            <p className="text-[10px] md:text-[11px] text-[#E5E4E2] opacity-70 tracking-wider">
              记录每一个转化时刻 · 见证时间的力量
            </p>
          </div>

          {/* Timeline */}
          <div className="relative">
            {/* Vertical Line */}
            <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-[1px] bg-[#D4AF37]/20" />

            {/* Timeline Nodes */}
            <div className="space-y-16 md:space-y-24">
              {timelineData.map((data, index) => (
                <div
                  key={data.year}
                  className="relative"
                  style={{
                    opacity: 0,
                    animation: `fadeInUp 0.8s ease-out ${index * 0.2}s forwards`
                  }}
                >
                  {/* Year Node */}
                  <div className="absolute left-0 md:left-1/2 md:-translate-x-1/2 -translate-y-1/2">
                    <button
                      onClick={() => setExpandedYear(expandedYear === data.year ? null : data.year)}
                      className="w-12 h-12 md:w-16 md:h-16 rounded-full border-2 border-[#D4AF37] bg-[#0A0A0A] flex items-center justify-center cursor-pointer hover:scale-110 transition-transform duration-300 whitespace-nowrap"
                    >
                      <span className="text-[10px] md:text-[12px] text-[#D4AF37] font-[500]">
                        {data.year}
                      </span>
                    </button>
                  </div>

                  {/* Content */}
                  <div className="md:grid md:grid-cols-2 md:gap-16">
                    <div className={`pl-16 md:pl-0 ${index % 2 === 0 ? 'md:text-right md:pr-24' : 'md:col-start-2 md:pl-24'}`}>
                      <div className="inline-block text-left md:text-left">
                        <div className="text-[12px] md:text-[14px] text-[#D4AF37] mb-2 md:mb-3 tracking-wide">
                          {data.season}
                        </div>
                        <div className="text-[9px] md:text-[10px] text-[#E5E4E2] opacity-70 mb-4 md:mb-6 tracking-wide">
                          {data.stage}
                        </div>

                        {expandedYear === data.year && (
                          <div className="space-y-4 md:space-y-6 mt-6 md:mt-8 animate-fadeIn">
                            {/* Climate Data */}
                            <div className="border border-[#D4AF37]/20 p-4 md:p-6 space-y-3">
                              <div className="text-[8px] md:text-[9px] text-[#E5E4E2] opacity-50 tracking-widest mb-3 md:mb-4">
                                环境数据
                              </div>
                              <div className="grid grid-cols-3 gap-3 md:gap-4">
                                <div>
                                  <div className="text-[7px] md:text-[8px] text-[#E5E4E2] opacity-50 mb-1">温度</div>
                                  <div className="text-[9px] md:text-[10px] text-[#D4AF37]">{data.climate.temp}</div>
                                </div>
                                <div>
                                  <div className="text-[7px] md:text-[8px] text-[#E5E4E2] opacity-50 mb-1">湿度</div>
                                  <div className="text-[9px] md:text-[10px] text-[#D4AF37]">{data.climate.humidity}</div>
                                </div>
                                <div>
                                  <div className="text-[7px] md:text-[8px] text-[#E5E4E2] opacity-50 mb-1">降雨</div>
                                  <div className="text-[9px] md:text-[10px] text-[#D4AF37]">{data.climate.rainfall}</div>
                                </div>
                              </div>
                            </div>

                            {/* Transformation */}
                            <div className="border border-[#D4AF37]/20 p-4 md:p-6">
                              <div className="text-[8px] md:text-[9px] text-[#E5E4E2] opacity-50 tracking-widest mb-2 md:mb-3">
                                转化阶段
                              </div>
                              <div className="text-[9px] md:text-[10px] text-[#E5E4E2] opacity-80 leading-relaxed tracking-wide">
                                {data.transformation}
                              </div>
                            </div>

                            {/* Microscopy */}
                            <div className="border border-[#D4AF37]/20 p-4 md:p-6">
                              <div className="text-[8px] md:text-[9px] text-[#E5E4E2] opacity-50 tracking-widest mb-2 md:mb-3">
                                显微观察
                              </div>
                              <div className="text-[9px] md:text-[10px] text-[#E5E4E2] opacity-80 leading-relaxed tracking-wide">
                                {data.microscopy}
                              </div>
                            </div>

                            {/* Microscopy Image */}
                            <div className="w-full h-40 md:h-48 border border-[#D4AF37]/20 overflow-hidden">
                              <img
                                src={`https://public.readdy.ai/ai/img_res/2377a7a00347f4afdace48259e97d2f4.jpg$%7Bdata.year%7D&width=400&height=300&seq=microscopy-${data.year}&orientation=landscape`}
                                alt={`${data.year}年显微图像`}
                                className="w-full h-full object-cover opacity-70 hover:opacity-100 transition-opacity duration-500"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }
      `}</style>
    </div>
  );
}